import { createContext, type ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react";
import type { UserDTO } from "@justone/shared";

/* ───────────── 아바타 ───────────── */

// 소나무 바탕에서 서로 구분되는 차분한 색
const AVATAR_COLORS = ["#2e7d63", "#3b6ea8", "#9a5b2e", "#7a4f9a", "#a8434f", "#4f7f2a", "#2d7f8f", "#8a6d1f"];

export function Avatar({ user, size = 36, presence = false }: { user: Pick<UserDTO, "id" | "displayName" | "online" | "avatarUrl"> | undefined; size?: number; presence?: boolean }) {
  const name = user?.displayName ?? "?";
  const color = AVATAR_COLORS[(user?.id ?? 0) % AVATAR_COLORS.length];
  return (
    <span className="avatar" style={{ width: size, height: size, background: color, fontSize: size * 0.42 }} aria-hidden="true">
      {user?.avatarUrl ? <img src={user.avatarUrl} alt="" width={size} height={size} loading="lazy" decoding="async" /> : Array.from(name)[0]}
      {presence && <span className={`presence ${user?.online ? "on" : ""}`} />}
    </span>
  );
}

/* ───────────── 대화상자 (네이티브 <dialog>) ───────────── */

export function Dialog({ open, onClose, children, label, role, className }: { open: boolean; onClose: () => void; children: ReactNode; label: string; role?: "alertdialog"; className?: string }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog
      ref={ref}
      className={className ? `dialog ${className}` : "dialog"}
      aria-label={label}
      role={role}
      onClose={onClose}
      onClick={(e) => {
        if (e.target === ref.current) onClose();
      }}
    >
      {open && children}
    </dialog>
  );
}

/* ───────────── 입력 필드 ───────────── */

export function Field({ id, label, hint, error, children }: { id: string; label: string; hint?: string; error?: string; children: ReactNode }) {
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      {children}
      {error ? (
        <span className="error" id={`${id}-error`}>
          {error}
        </span>
      ) : hint ? (
        <span className="hint">{hint}</span>
      ) : null}
    </div>
  );
}

/* ───────────── 토스트 ───────────── */

/** 함수면 토스트 전체를 누르면 실행, { label, run }이면 그 이름의 버튼 (되돌리기·열기 등) */
export type ToastAction = (() => void) | { label: string; run: () => void };
type ShowToast = (message: string, action?: ToastAction) => void;
const ToastContext = createContext<ShowToast>(() => {});
export const useToast = () => useContext(ToastContext);

interface ToastItem {
  id: number;
  message: string;
  action?: ToastAction;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());
  const remove = useCallback((id: number) => {
    clearTimeout(timers.current.get(id));
    timers.current.delete(id);
    setItems((xs) => xs.filter((x) => x.id !== id));
  }, []);
  // 동작이 있는 토스트는 더 오래, 마우스를 올리거나 포커스가 있는 동안은 사라지지 않는다
  const schedule = useCallback((t: ToastItem) => timers.current.set(t.id, setTimeout(() => remove(t.id), t.action ? 7000 : 3500)), [remove]);
  const show = useCallback<ShowToast>(
    (message, action) => {
      const t = { id: Date.now() + Math.random(), message, action };
      setItems((xs) => [...xs.slice(-2), t]);
      schedule(t);
    },
    [schedule],
  );
  const hold = (t: ToastItem) => clearTimeout(timers.current.get(t.id));
  return (
    <ToastContext.Provider value={show}>
      {children}
      <div className="toast-stack" role="status" aria-live="polite">
        {items.map((t) => {
          const events = { onMouseEnter: () => hold(t), onMouseLeave: () => schedule(t), onFocus: () => hold(t), onBlur: () => schedule(t) };
          if (typeof t.action === "function") {
            const run = t.action;
            return (
              <button
                key={t.id}
                type="button"
                className="toast toast-action"
                {...events}
                onClick={() => {
                  remove(t.id);
                  run();
                }}
              >
                {t.message}
              </button>
            );
          }
          const action = t.action;
          return (
            <div key={t.id} className="toast" {...events}>
              <span className="toast-text">{t.message}</span>
              {action && (
                <button
                  type="button"
                  className="toast-btn"
                  onClick={() => {
                    remove(t.id);
                    action.run();
                  }}
                >
                  {action.label}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

/* ───────────── 확인 (브라우저 confirm 대신) ───────────── */

export interface ConfirmOptions {
  title: string;
  body?: ReactNode;
  /** 확인 버튼 이름: "삭제", "비활성화" 등 하는 일 그대로 */
  confirmLabel: string;
  /** 되돌릴 수 없는 일: 빨간 버튼, 처음 포커스는 취소 */
  danger?: boolean;
  /** 이 글자를 그대로 입력해야 확인할 수 있다 (채널 삭제 등) */
  typeToConfirm?: string;
}

type Confirm = (o: ConfirmOptions) => Promise<boolean>;
const ConfirmContext = createContext<Confirm>(async () => false);
export const useConfirm = () => useContext(ConfirmContext);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [req, setReq] = useState<(ConfirmOptions & { resolve: (v: boolean) => void }) | null>(null);
  const confirm = useCallback<Confirm>((o) => new Promise((resolve) => setReq({ ...o, resolve })), []);
  const done = (v: boolean) => {
    req?.resolve(v);
    setReq(null);
  };
  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {req && <ConfirmDialog options={req} onDone={done} />}
    </ConfirmContext.Provider>
  );
}

function ConfirmDialog({ options: o, onDone }: { options: ConfirmOptions; onDone: (v: boolean) => void }) {
  const [typed, setTyped] = useState("");
  const ready = !o.typeToConfirm || typed.trim() === o.typeToConfirm;
  return (
    <Dialog open onClose={() => onDone(false)} label={o.title} role="alertdialog">
      <form
        className="confirm"
        onSubmit={(e) => {
          e.preventDefault();
          if (ready) onDone(true);
        }}
      >
        <h2>{o.title}</h2>
        {o.body && <div className="confirm-body">{o.body}</div>}
        {o.typeToConfirm && (
          <Field id="confirm-type" label={`확인하려면 ${o.typeToConfirm}을(를) 입력하세요`}>
            <input id="confirm-type" className="input" value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" autoFocus />
          </Field>
        )}
        <div className="buttons">
          <button type="button" className="btn" onClick={() => onDone(false)} autoFocus={o.danger && !o.typeToConfirm}>
            취소
          </button>
          <button type="submit" className={`btn ${o.danger ? "btn-danger-solid" : "btn-primary"}`} disabled={!ready} autoFocus={!o.danger && !o.typeToConfirm}>
            {o.confirmLabel}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

/* ───────────── 아이콘 ───────────── */

const paths = {
  plus: "M12 5v14M5 12h14",
  menu: "M4 7h16M4 12h16M4 17h16",
  more: "M6 12h.01M12 12h.01M18 12h.01",
  compass: "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm3.5-12.5-2 5-5 2 2-5 5-2Z",
  send: "M4 12l16-8-6 16-2.5-6.5L4 12Z",
  arrowDown: "M12 5v14M6 13l6 6 6-6",
  chevronUp: "M6 15l6-6 6 6",
  close: "M6 6l12 12M18 6L6 18",
  reply: "M9 14L4 9l5-5M4 9h10a6 6 0 0 1 6 6v5",
  smile: "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM8.5 14.5s1.3 2 3.5 2 3.5-2 3.5-2M9 9.5h.01M15 9.5h.01",
  threads: "M4 5h16v10H8l-4 4V5ZM8 9h8M8 12h5",
  bell: "M18 16v-5a6 6 0 1 0-12 0v5l-2 2h16l-2-2ZM10 20a2 2 0 0 0 4 0",
  check: "M5 12l5 5L20 7",
  clip: "M21 11.5l-8.6 8.6a5.5 5.5 0 0 1-7.8-7.8l8.6-8.6a3.7 3.7 0 0 1 5.2 5.2l-8.6 8.6a1.8 1.8 0 0 1-2.6-2.6l7.9-7.9",
  search: "M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14Zm9 2-3.5-3.5",
  inbox: "M4 13h4l2 3h4l2-3h4M5.5 5h13L20 13v6H4v-6l1.5-8Z",
  cloudOff: "M3 3l18 18M8.5 8.6A5 5 0 0 0 7 18h10.5M16.5 9.5A4 4 0 0 1 20 16",
};

export type IconName = keyof typeof paths;

export function Icon({ name, label }: { name: IconName; label?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={name === "more" ? 3 : 1.8} strokeLinecap="round" strokeLinejoin="round" role={label ? "img" : undefined} aria-label={label} aria-hidden={label ? undefined : true}>
      <path d={paths[name]} />
    </svg>
  );
}

/** 바깥을 누르면 닫히는 메뉴용 */
export function useDismiss<T extends HTMLElement>(open: boolean, onClose: () => void) {
  const ref = useRef<T>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);
  return ref;
}

/* ───────────── 불러오는 동안의 틀 ───────────── */

const WIDTHS = [92, 74, 86, 60, 80, 68, 88, 54];

/** 내용 모양의 회색 틀 (글자 대신). 움직임 줄이기 설정이면 반짝이지 않는다 */
export function Skeleton({ kind, rows = 5 }: { kind: "messages" | "list" | "page" | "board" | "lines"; rows?: number }) {
  const w = (i: number) => `${WIDTHS[i % WIDTHS.length]}%`;
  let body: ReactNode;
  if (kind === "messages")
    body = Array.from({ length: rows }, (_, i) => (
      <div key={i} className="sk-msg">
        <span className="skel sk-avatar" />
        <span className="sk-col">
          <span className="skel sk-line" style={{ width: 110 }} />
          <span className="skel sk-line" style={{ width: w(i) }} />
          {i % 2 === 0 && <span className="skel sk-line" style={{ width: w(i + 3) }} />}
        </span>
      </div>
    ));
  else if (kind === "list")
    body = Array.from({ length: rows }, (_, i) => (
      <div key={i} className="sk-row">
        <span className="skel sk-dot" />
        <span className="skel sk-line" style={{ width: w(i) }} />
      </div>
    ));
  else if (kind === "board")
    body = (
      <div className="sk-board">
        {[3, 2, 1].map((n, c) => (
          <div key={c} className="sk-colm">
            <span className="skel sk-line" style={{ width: 70 }} />
            {Array.from({ length: n }, (_, i) => (
              <span key={i} className="skel sk-card" />
            ))}
          </div>
        ))}
      </div>
    );
  else if (kind === "page")
    body = (
      <>
        <span className="skel sk-title" />
        {Array.from({ length: rows }, (_, i) => (
          <span key={i} className="skel sk-line" style={{ width: w(i) }} />
        ))}
      </>
    );
  else body = Array.from({ length: rows }, (_, i) => <span key={i} className="skel sk-line" style={{ width: w(i) }} />);
  return (
    <div className={`skeleton sk-${kind}`} role="status" aria-label="불러오는 중" aria-busy="true">
      {body}
    </div>
  );
}

/* ───────────── 빈 화면 ───────────── */

/** 비었을 때: 무엇이 비었는지와 다음에 할 일 */
export function EmptyState({ icon, title, children, action }: { icon?: IconName; title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty-state">
      {icon && (
        <span className="es-icon">
          <Icon name={icon} />
        </span>
      )}
      <p className="es-title">{title}</p>
      {children && <div className="es-body">{children}</div>}
      {action && <div className="es-action">{action}</div>}
    </div>
  );
}
